import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Spinner } from 'flowbite-react';

/**
 * ProtectedRoute component
 * 
 * Wraps routes that require authentication
 * If the user is not authenticated, redirects to the login page
 * While checking authentication status, displays a loading spinner
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The route content to render if authenticated
 * @param {string} props.redirectTo - Where to redirect if not authenticated (default: "/login")
 * @returns {React.ReactNode} The protected route content or redirect
 */
const ProtectedRoute = ({ children, redirectTo = "/login" }) => {
  const { user, loading } = useAuth();
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // When auth loading is complete, mark auth as checked
    if (!loading) {
      setAuthChecked(true);
    }
  }, [loading]);

  // While checking authentication, show loading spinner
  if (loading || !authChecked) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Card className="max-w-sm">
          <div className="text-center p-6">
            <Spinner size="xl" className="mx-auto mb-4" />
            <h5 className="text-lg font-medium text-gray-900 mb-1">Checking authentication...</h5>
            <p className="text-gray-500 text-sm">Please wait while we verify your credentials.</p>
          </div>
        </Card>
      </div>
    );
  }

  // If authenticated, check subscription status
  if (user) {
    // Check if user has active subscriptions
    const hasActiveSubscriptions = user.has_active_subscriptions;

    console.log('🔍 ProtectedRoute - User subscription status:', {
      userId: user.id,
      email: user.email,
      hasActiveSubscriptions,
      hasActiveSubscriptionsType: typeof hasActiveSubscriptions,
      currentPath: location.pathname,
      fullUser: user
    });
    
    // Business users skip subscription check — they use /business routes
    const isBusinessUser = !!user.business_account_id;

    // Billing is reachable without an active subscription: it manages cards and
    // hosts the dev→live upgrade banner that the trial-limit email links to.
    // Without this, that email link would bounce to /newplan instead.
    const isBillingPage = location.pathname.startsWith('/dashboard/billing');

    // If user doesn't have active subscriptions and is not already on newplan or pixel dashboard page
    if (!isBusinessUser && !hasActiveSubscriptions && !isBillingPage && !location.pathname.startsWith('/newplan') && !location.pathname.startsWith('/pixel/') && !location.pathname.startsWith('/business')) {
      console.log('Redirecting to /newplan - no active subscriptions');
      return (
        <Navigate
          to="/newplan"
          state={{ from: location.pathname, message: "Please select a plan to continue" }}
          replace
        />
      );
    }
    
    // If user has active subscriptions or is on newplan page, render content
    return children;
  }

  // If not authenticated, redirect to login page with return URL
  return (
    <Navigate
      to={redirectTo}
      state={{ from: location.pathname, message: "Please log in to access this page" }}
      replace
    />
  );
};

export default ProtectedRoute;